// Delivery tab — county breakdown + map + couriers.
import { useEffect, useState } from "react";
import { getDelivery } from "@/lib/analytics";
import { fmtInt, fmtPct, fmtMoney, KpiCard } from "../components/KpiCard";
import { Card } from "./OverviewTab";
import RomaniaCountyMap from "@/pages/delivery/RomaniaCountyMap";
import { Truck, CheckCircle2, Clock, XCircle, Timer, MapPin } from "lucide-react";

const COLOR = { primary: "#E91E63", emerald: "#22C55E", amber: "#F59E0B", rose: "#F43F5E" };

export default function DeliveryTab({ range }) {
  const [data, setData] = useState(null);
  const [metric, setMetric] = useState("orders");
  useEffect(() => { getDelivery(range).then(setData); }, [JSON.stringify(range)]); // eslint-disable-line
  if (!data) return <div className="h-96 rounded-xl bg-card/40 border border-border animate-pulse" />;
  const t = data.totals;
  return (
    <div className="space-y-6" data-testid="analytics-delivery">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KpiCard label="Total comenzi" value={t.orders} icon={Truck} color={COLOR.primary} />
        <KpiCard label="Livrate" value={t.delivered} icon={CheckCircle2} color={COLOR.emerald} />
        <KpiCard label="În așteptare" value={t.pending} icon={Clock} color={COLOR.amber} />
        <KpiCard label="Refuzate / retur" value={t.refused} icon={XCircle} color={COLOR.rose} />
        <KpiCard label="Rată livrare" value={t.rate} format="pct" icon={Truck} color={COLOR.emerald} />
      </div>

      {t.avg_days != null && (
        <div className="rounded-xl bg-card/70 border border-border p-4 flex items-center gap-3">
          <Timer className="h-5 w-5 text-primary" />
          <div>
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Timp mediu livrare</div>
            <div className="text-2xl font-semibold tabular-nums">{t.avg_days.toFixed(1)} <span className="text-sm text-muted-foreground font-normal">zile</span></div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[11px] uppercase tracking-wider text-muted-foreground">Hartă România —</span>
            {[
              { id: "orders", label: "Comenzi" },
              { id: "delivered", label: "Livrate" },
              { id: "refused", label: "Refuzate" },
              { id: "revenue", label: "Vânzări" },
            ].map((m) => (
              <button
                key={m.id}
                onClick={() => setMetric(m.id)}
                data-testid={`map-metric-${m.id}`}
                className={`text-[11px] px-2 py-0.5 rounded-full ${metric === m.id ? "bg-primary text-primary-foreground" : "bg-secondary/50 text-muted-foreground"}`}
              >
                {m.label}
              </button>
            ))}
          </div>
          <RomaniaCountyMap data={data.counties} metric={metric} title="" />
        </div>

        <Card title="Tabel județe (sortat după rată de livrare)">
          <div className="max-h-[480px] overflow-auto">
            <table className="w-full text-sm">
              <thead className="text-[11px] uppercase text-muted-foreground sticky top-0 bg-card">
                <tr>
                  <th className="text-left py-2 px-2">Județ</th>
                  <th className="text-right">Comenzi</th>
                  <th className="text-right">Livr.</th>
                  <th className="text-right">Aștep.</th>
                  <th className="text-right">Refuz.</th>
                  <th className="text-right">Vânzări</th>
                  <th className="text-right pr-2">Rată</th>
                </tr>
              </thead>
              <tbody>
                {data.counties.map((c) => (
                  <tr key={c.county} className="border-t border-border/50 hover:bg-secondary/20">
                    <td className="py-1.5 px-2">{c.county}</td>
                    <td className="text-right tabular-nums">{fmtInt(c.orders)}</td>
                    <td className="text-right tabular-nums text-emerald-500">{fmtInt(c.delivered)}</td>
                    <td className="text-right tabular-nums text-amber-500">{fmtInt(c.pending)}</td>
                    <td className="text-right tabular-nums text-rose-500">{fmtInt(c.refused)}</td>
                    <td className="text-right tabular-nums">{fmtMoney(c.revenue)}</td>
                    <td className="text-right tabular-nums font-semibold pr-2">{c.rate.toFixed(1)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <Card title="Județe problematice">
        {data.problematic.length === 0 ? (
          <div className="text-center text-xs text-muted-foreground py-8">Niciun județ cu rată de problemă semnificativă</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.problematic.map((c) => (
              <div key={c.county} className="rounded-lg bg-rose-500/5 border border-rose-500/30 p-3 flex items-center gap-3">
                <MapPin className="h-4 w-4 text-rose-500 shrink-0" />
                <div className="flex-1">
                  <div className="font-medium">{c.county}</div>
                  <div className="text-[11px] text-muted-foreground">
                    {fmtInt(c.refused)} refuzate · {fmtInt(c.pending)} pending · {fmtInt(c.orders)} total
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-rose-400 font-bold">{(((c.pending + c.refused) / Math.max(1, c.orders)) * 100).toFixed(0)}%</div>
                  <div className="text-[10px] text-muted-foreground">probleme</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
